// ============================================================================
// minio-url.ts — cross-device file-URL hardening (2026-06-05)
// ----------------------------------------------------------------------------
// Detect + deconstruct raw MinIO URLs so the render layer can convert them to
// same-origin proxy URLs. A raw MinIO URL points at the host-published MinIO
// port (e.g. http://localhost:9010/quotemaker-files/<s3Key>?X-Amz-...) and
// fails with ERR_CONNECTION_REFUSED from any device that can't reach that port
// (the Customer-Zero "works on my box, broken on the other device" bug).
//
// Stored project scopes can carry these URLs on photo records (persisted before
// the same-origin proxy existed), so the render path must rewrite them at
// display time. Same-origin proxy URLs (relative /api/files/p/<token>) and
// data: / blob: URLs are always reachable and pass through untouched.
// ============================================================================

// The S3 bucket path segment present in every raw MinIO file URL. Matches the
// frontend's FILES_BUCKET_PATH_PREFIX (data-service-api.js) and the backend
// MINIO_BUCKET (quotemaker-files).
export const FILES_BUCKET_PATH = '/quotemaker-files/';

/**
 * True if `url` is a raw absolute MinIO URL (host-published port + bucket path)
 * the browser may not be able to reach from another device. Same-origin proxy
 * URLs (relative `/api/files/...`), `data:`, and `blob:` URLs return false.
 *
 * Typed as a `url is string` predicate so callers (e.g. s3KeyFromMinioUrl)
 * get the same narrowing the runtime typeof check already performs.
 */
export function isRawMinioUrl(url: unknown): url is string {
    return typeof url === 'string'
        && /^https?:\/\//i.test(url)
        && url.includes(FILES_BUCKET_PATH);
}

/**
 * Extract the s3 key from a raw MinIO URL — the path after the bucket segment,
 * query string stripped, URL-decoded. Returns null for non-MinIO / malformed
 * URLs.
 */
export function s3KeyFromMinioUrl(url: unknown): string | null {
    if (!isRawMinioUrl(url)) return null;
    try {
        const { pathname } = new URL(url);
        const at = pathname.indexOf(FILES_BUCKET_PATH);
        if (at === -1) return null;
        const key = pathname.slice(at + FILES_BUCKET_PATH.length);
        return key ? decodeURIComponent(key) : null;
    } catch {
        return null;
    }
}
